import { atom, type Atom } from "jotai";
import type { createStore } from "jotai";
import type { MicDevice } from "../hooks/useRealtimeTranscription";

export type DictationStage =
  | "idle"
  | "connecting"
  | "ready"
  | "listening"
  | "finalizing"
  | "delivering";

export type DictationMode = "smart" | "literal" | "voice_adjust";

export type DictationDispatch =
  | {
      kind: "insert";
      targetChipId: string | null;
      pos: number | null;
      sectionKind: string | null;
    }
  | { kind: "command"; command: string; payload?: unknown }
  | {
      kind: "dropped";
      reason:
        | "no-focus"
        | "empty"
        | "editor-gone"
        | "stale-chip"
        | "auth"
        | "postprocess"
        | "device_switch"
        | "delivery";
    };

export interface DictationHistoryEntry {
  ts: number;
  text: string;
  postprocessed: string;
  mode: DictationMode;
  dispatch: DictationDispatch;
}

export interface DictationDeviceState {
  selectedId: string | null;
  available: MicDevice[];
  quality: "wideband" | "narrowband" | null;
  appliedConstraints: {
    sampleRate: number | null;
    echoCancellation: boolean | null;
    noiseSuppression: boolean | null;
    autoGainControl: boolean | null;
  } | null;
  inputLevel: number | null;
}

export interface DictationSession {
  stage: DictationStage;
  mode: DictationMode;
  device: DictationDeviceState;
  lastInterim: { text: string; ts: number } | null;
  // Finalized transcript segments accumulated during the current hold.
  // Sole source of truth — useRealtimeTranscription writes the completed
  // transcript here, reads it in deliverTranscript to hand the joined
  // string to onFinalTranscript (i.e. what the LLM sees). Reset on the
  // next start() / cancel(). Persists across finalizing → delivering →
  // ready so the mic test panel can observe each stage.
  liveFinals: string[];
  history: DictationHistoryEntry[];
  error: string | null;
}

export const HISTORY_CAP = 10;

const initialSession: DictationSession = {
  stage: "idle",
  mode: "smart",
  device: {
    selectedId: null,
    available: [],
    quality: null,
    appliedConstraints: null,
    inputLevel: null,
  },
  lastInterim: null,
  liveFinals: [],
  history: [],
  error: null,
};

export const dictationSessionAtom = atom<DictationSession>(initialSession);

export const dictationStageAtom: Atom<DictationStage> = atom(
  (get) => get(dictationSessionAtom).stage,
);
export const dictationDeviceAtom: Atom<DictationDeviceState> = atom(
  (get) => get(dictationSessionAtom).device,
);
export const dictationHistoryAtom: Atom<DictationHistoryEntry[]> = atom(
  (get) => get(dictationSessionAtom).history,
);
export const dictationErrorAtom: Atom<string | null> = atom(
  (get) => get(dictationSessionAtom).error,
);

/**
 * Canonical two-zone live transcript:
 *   - finalized: the completed transcript of the current hold. This is the
 *                string delivered to onFinalTranscript (i.e. what the LLM
 *                receives). Reset only on the next start() — persists across
 *                finalizing → delivering → ready so the mic test panel can
 *                observe each stage.
 *   - interim:   the text streamed so far this hold (grows with each delta,
 *                cleared on completion / stop / cancel).
 * Both the editor ghost text and the mic test panel render off the live
 * combo so the panel is a faithful verification of the editor UX.
 */
export const dictationFinalizedTranscriptAtom: Atom<string> = atom((get) =>
  get(dictationSessionAtom).liveFinals.join(" "),
);

export interface DictationLiveTranscript {
  finalized: string;
  interim: string;
}

export const dictationLiveTranscriptAtom: Atom<DictationLiveTranscript> = atom((get) => {
  const s = get(dictationSessionAtom);
  return {
    finalized: s.liveFinals.join(" "),
    interim: s.lastInterim?.text ?? "",
  };
});

type Store = ReturnType<typeof createStore>;

/**
 * Append a history entry and cap the buffer at HISTORY_CAP.
 * Sole writer of the history slot.
 */
export function appendDictationHistory(store: Store, entry: DictationHistoryEntry): void {
  store.set(dictationSessionAtom, (s) => {
    const next = [...s.history, entry];
    if (next.length > HISTORY_CAP) next.splice(0, next.length - HISTORY_CAP);
    return { ...s, history: next };
  });
}
